import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { useCategories, useServices } from "@/lib/queries";
import { getIcon } from "@/lib/icons";
import { breadcrumbSchema } from "@/lib/schema";
import FinalCta from "@/sections/FinalCta";

export default function Services() {
  const { data: categories = [] } = useCategories();
  const { data: services = [] } = useServices();

  const byCat = {};
  services.forEach((s) => {
    (byCat[s.category_slug] = byCat[s.category_slug] || []).push(s);
  });
  const activeCats = categories.filter((c) => (byCat[c.slug] || []).length > 0);

  return (
    <>
      <Seo
        title="Hizmetlerimiz | Meguiar's İzmit Araç Koruma ve Bakım"
        description="Seramik kaplama, PPF, boya koruma, detaylı iç temizlik, cam filmi, jant ve far bakımı dahil tüm araç koruma hizmetleri. Kocaeli Meguiar's İzmit."
        canonicalPath="/hizmetler"
        jsonLd={breadcrumbSchema([{ label: "Ana Sayfa", to: "/" }, { label: "Hizmetler" }])}
      />
      <PageHero
        overline="Hizmetlerimiz"
        title="Aracınız için eksiksiz koruma yelpazesi"
        subtitle="8 ana kategori altında, boya korumadan iç hijyene kadar profesyonel araç bakım çözümleri. Her hizmet, detaylı bilgisiyle ayrı bir sayfada."
        breadcrumbs={[{ label: "Ana Sayfa", to: "/" }, { label: "Hizmetler" }]}
      />

      {/* Sticky category nav */}
      <div className="sticky top-[68px] z-30 border-b border-white/10 bg-black/80 backdrop-blur-xl">
        <div className="container-x flex gap-2 overflow-x-auto py-3.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {activeCats.map((c) => (
            <a
              key={c.slug}
              href={`#cat-${c.slug}`}
              data-testid={`services-nav-${c.slug}`}
              className="shrink-0 border border-white/10 px-4 py-2 font-oswald text-xs uppercase tracking-[0.12em] text-neutral-300 transition-colors hover:border-gold/50 hover:text-gold"
            >
              {c.name}
            </a>
          ))}
        </div>
      </div>

      <section className="obsidian py-20 sm:py-28">
        <div className="container-x space-y-24">
          {activeCats.map((c, ci) => {
            const Icon = getIcon(c.icon);
            const list = byCat[c.slug] || [];
            return (
              <div key={c.slug} id={`cat-${c.slug}`} className="scroll-mt-32">
                <Reveal>
                  <div className="flex flex-col gap-5 border-b border-white/10 pb-7 sm:flex-row sm:items-end sm:justify-between">
                    <div className="flex items-start gap-4">
                      <span className="font-oswald text-sm font-bold tracking-[0.2em] text-gold">{String(ci + 1).padStart(2, "0")}</span>
                      <div>
                        <div className="flex items-center gap-3">
                          <span className="flex h-10 w-10 items-center justify-center border border-gold/30 bg-gold/10 text-gold"><Icon className="h-5 w-5" /></span>
                          <h2 className="font-display text-2xl font-medium text-white sm:text-3xl">{c.name}</h2>
                        </div>
                        <p className="mt-3 max-w-2xl text-sm text-neutral-400">{c.short}</p>
                      </div>
                    </div>
                    <Link to={`/hizmetler/kategori/${c.slug}`} data-testid={`services-cat-link-${c.slug}`} className="group inline-flex shrink-0 items-center gap-2 font-oswald text-xs uppercase tracking-[0.15em] text-neutral-400 transition-colors hover:text-gold">
                      Kategori sayfası <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </Link>
                  </div>
                </Reveal>

                <div className="mt-8 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {list.map((s, i) => (
                    <Reveal key={s.slug} delay={(i % 3) * 0.07}>
                      <Link
                        to={`/hizmetler/${s.slug}`}
                        data-testid={`service-link-${s.slug}`}
                        className="group flex h-full flex-col overflow-hidden border border-white/10 bg-neutral-900 transition-colors hover:border-gold/40"
                      >
                        <div className="relative aspect-[16/10] overflow-hidden">
                          <img src={`${s.card_image || s.hero_image}?w=640`} alt={s.name} loading="lazy" decoding="async" className="h-full w-full object-cover grayscale-[0.2] transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0" />
                          <div className="absolute inset-0 bg-gradient-to-t from-neutral-900 via-neutral-900/30 to-transparent" />
                        </div>
                        <div className="flex flex-1 flex-col p-5">
                          <h3 className="font-display text-lg font-medium text-white transition-colors group-hover:text-gold">{s.name}</h3>
                          <p className="mt-2 flex-1 text-sm text-neutral-400 line-clamp-2">{s.excerpt}</p>
                          <span className="mt-4 inline-flex items-center gap-1.5 font-oswald text-xs uppercase tracking-[0.15em] text-gold">İncele <ArrowUpRight className="h-3.5 w-3.5" /></span>
                        </div>
                      </Link>
                    </Reveal>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>
      <FinalCta />
    </>
  );
}
